import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { StripeConnectCard } from "@/components/app/StripeConnectCard";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Euro, Clock, RotateCcw, Ban, FileDown } from "lucide-react";

const LABEL = {
  card_required: ["Carte à enregistrer", "bg-slate-100 text-slate-600"],
  card_registered: ["Carte enregistrée · débit après consult", "bg-blue-50 text-blue-700"],
  paid: ["Encaissé", "bg-emerald-50 text-emerald-700"],
  failed: ["Paiement refusé", "bg-red-50 text-red-700"],
  waived: ["Non facturé", "bg-slate-100 text-slate-500"],
  refunded: ["Remboursé", "bg-amber-50 text-amber-700"],
  released: ["Annulé · aucun débit", "bg-slate-100 text-slate-500"],
};

export default function DoctorPayments() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/doctor/payments").then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e?.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(id + action);
    try {
      await api.post(`/appointments/${id}/${action}`);
      toast.success(action === "charge" ? "Paiement encaissé" : action === "refund" ? "Remboursement effectué" : "Rendez-vous non facturé");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const receipt = async (a) => {
    try {
      const res = await api.get(`/appointments/${a.appointment_id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `note-honoraires-${a.start.slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Téléconsultations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Encaissements</h1>
        <p className="text-slate-500 mt-2">Cartes enregistrées à la réservation, débit automatique à la fin de la consultation. Les fonds vont directement sur votre compte bancaire.</p>
      </div>

      <div className="mb-6"><StripeConnectCard /></div>

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="payments-month-total">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><Euro className="h-3.5 w-3.5" />Encaissé ce mois</div>
              <div className="font-display text-2xl font-extrabold text-deep mt-1">{data.month_total_eur.toFixed(2).replace(".", ",")} €</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="payments-pending-count">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Cartes en attente de débit</div>
              <div className="font-display text-2xl font-extrabold text-deep mt-1">{data.pending_count}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" />Commission Doctobook</div>
              <div className="font-display text-2xl font-extrabold text-emerald-700 mt-1">0 €</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="payments-table">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="payments-empty">Aucune téléconsultation avec paiement en ligne pour le moment.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.items.map((a) => {
                  const [label, cls] = LABEL[a.payment_status] || [a.payment_status, "bg-slate-100 text-slate-600"];
                  const started = new Date(a.start) <= new Date();
                  const canCharge = a.payment_status === "card_registered" && (started || a.late_cancellation);
                  return (
                    <div key={a.appointment_id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`payment-row-${a.appointment_id}`}>
                      <div className="flex-1 min-w-[220px]">
                        <div className="font-semibold text-deep">{a.patient_name || "Patient"} <span className="text-slate-400 font-normal">· {a.fee_eur} €</span></div>
                        <div className="text-xs text-slate-500">
                          {new Date(a.start).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                          {a.card_last4 && <> · {(a.card_brand || "").toUpperCase()} •••• {a.card_last4}</>}
                          {a.late_cancellation && <span className="ml-2 text-amber-700 font-semibold">Annulation tardive (&lt; 24 h)</span>}
                          {a.status === "cancelled" && !a.late_cancellation && <span className="ml-2">Annulé</span>}
                          {a.auto_charged && <span className="ml-2 text-emerald-700">débit automatique</span>}
                          {a.payment_error && <span className="ml-2 text-red-600">{a.payment_error}</span>}
                        </div>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`payment-status-${a.appointment_id}`}>{label}</span>
                      <div className="flex gap-2">
                        {canCharge && (
                          <Button size="sm" onClick={() => act(a.appointment_id, "charge")} disabled={!!busy} className="btn-primary" data-testid={`charge-${a.appointment_id}-btn`}>
                            <Euro className="h-3.5 w-3.5 mr-1" />Encaisser {a.fee_eur} €
                          </Button>
                        )}
                        {["card_registered", "failed"].includes(a.payment_status) && (
                          <Button size="sm" variant="ghost" onClick={() => act(a.appointment_id, "waive", "Ne pas facturer ce rendez-vous ?")} disabled={!!busy} className="text-slate-500" data-testid={`waive-${a.appointment_id}-btn`}>
                            <Ban className="h-3.5 w-3.5 mr-1" />Ne pas facturer
                          </Button>
                        )}
                        {a.payment_status === "paid" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => receipt(a)} data-testid={`receipt-${a.appointment_id}-btn`}>
                              <FileDown className="h-3.5 w-3.5 mr-1" />Note d'honoraires
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => act(a.appointment_id, "refund", `Rembourser ${a.fee_eur} € au patient ?`)} disabled={!!busy} data-testid={`refund-${a.appointment_id}-btn`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />Rembourser
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
